import React from 'react';
import PropTypes from 'prop-types';

// Confidence, and the decision an operator makes about one finding.
//
// Rendered under each part in the report. Its own component rather than more
// lines inside InspectionReport's render, which is already long enough that
// nobody reads the middle of it.
//
// Shows nothing at all when the finding was not queued: a high-confidence
// result needs no decision, and putting buttons on it would invite operators to
// review everything, which is the state this replaces.
//
// Styling is inline rather than by utility class. package.json pins bootstrap
// 3.4.1, where fw-bold, rounded-3, me-2 and the bg-* badge classes do not
// exist — the surrounding screen uses them anyway, so either something serves
// a later Bootstrap at runtime or that screen has been rendering half-styled.
// This component should not depend on which, since an operator deciding whether
// a customer gets charged needs to be able to read it either way. Only class
// names that mean the same thing in Bootstrap 3, 4 and 5 are used: btn,
// btn-success, btn-danger and form-control.

// What the reason codes mean to somebody who has to act on one.
const REASON_TEXT = {
  low_confidence: 'The model was not sure about this one.',
  not_a_vehicle: 'The model says these photographs are not of a real vehicle.',
  unassessable: 'The model could not judge this part.',
  high_severity: 'Severe damage always gets a second look before it becomes a charge.',
};

const styles = {
  wrapper: { marginTop: '16px' },
  badge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 'bold',
  },
  panel: {
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '16px',
    marginTop: '12px',
    backgroundColor: '#fbfbfb',
  },
  heading: { fontWeight: 'bold', marginBottom: '4px' },
  reason: { color: '#666', marginBottom: '12px' },
  note: { marginBottom: '12px' },
  confirm: { marginRight: '8px' },
  decided: { marginTop: '12px', color: '#666' },
};

// Green when the model was sure, amber when it was not, grey when nobody
// recorded a value. Inline for the same reason as everything else here.
const BADGE_COLOURS = { high: '#2d8f4b', medium: '#0d6f8f', low: '#b8860b', unknown: '#6c757d' };

class FindingReview extends React.Component {
  static propTypes = {
    // The queue row for this finding, or null when it was not queued.
    review: PropTypes.object,
    // Confidence from the report payload itself, shown even when no decision is
    // needed — an operator reading a report wants to know how sure the model
    // was, not only whether it crossed a threshold.
    confidence: PropTypes.number,
    onDecide: PropTypes.func.isRequired,
    busy: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = { note: '' };
  }

  handleNote = (e) => this.setState({ note: e.target.value });

  decide = (state) => this.props.onDecide(this.props.review.id, state, this.state.note);

  // Percentages, not the raw 0-1 float. "82%" is read correctly at a glance and
  // "0.82" is read as a score out of one by half the people who see it.
  confidenceLabel(value) {
    if (value === null || value === undefined) return 'not recorded';
    return `${Math.round(value * 100)}%`;
  }

  render() {
    const { review, confidence, busy } = this.props;

    // Reports generated before the analysis service persisted confidence carry
    // no value at all. Saying so is more useful than printing 0%, which reads
    // as "the model was certain it did not know".
    const score = confidence === null || confidence === undefined
      ? (review ? review.confidence : null)
      : confidence;

    const band = score === null || score === undefined
      ? 'unknown'
      : score >= 0.9 ? 'high' : score >= 0.7 ? 'medium' : 'low';

    return (
      <div style={styles.wrapper}>
        <span style={{ ...styles.badge, backgroundColor: BADGE_COLOURS[band] }}>
          Model confidence: {this.confidenceLabel(score)}
        </span>

        {review && review.reviewState === 'unreviewed' && (
          <div style={styles.panel}>
            <div style={styles.heading}>This finding needs a decision</div>
            <div style={styles.reason}>{REASON_TEXT[review.reviewReason] || 'Flagged for review.'}</div>

            <input
              type="text"
              className="form-control"
              style={styles.note}
              placeholder="Why? — e.g. shadow, not a dent (optional, but it is the useful part)"
              value={this.state.note}
              onChange={this.handleNote}
              disabled={busy}
            />

            <button
              type="button"
              className="btn btn-success"
              style={styles.confirm}
              onClick={() => this.decide('confirmed')}
              disabled={busy}
            >
              Damage is real
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => this.decide('rejected')}
              disabled={busy}
            >
              Not damage
            </button>
          </div>
        )}

        {review && review.reviewState !== 'unreviewed' && (
          <div style={styles.decided}>
            {review.reviewState === 'confirmed' ? 'Confirmed' : 'Rejected'}
            {review.reviewedAt ? ` on ${new Date(Number(review.reviewedAt) || review.reviewedAt).toLocaleString()}` : ''}
            {review.reviewNote ? ` — "${review.reviewNote}"` : ''}
          </div>
        )}
      </div>
    );
  }
}

export default FindingReview;
